import { useEffect, useRef } from "react";
import { useBreadcrumbContextOptional } from "../contexts/Breadcrumb/BreadcrumbProvider";
import type { UseBreadcrumbConfig } from "../contexts/Breadcrumb/types";

/**
 * Declarative hook for page-level breadcrumb registration.
 *
 * Each page calls this with its own breadcrumb entry. The provider
 * handles trail accumulation, reset-point detection, and truncation automatically.
 *
 * Gracefully no-ops when called outside a BreadcrumbProvider (e.g. guest/SCORM flows).
 *
 * @param config - This page's breadcrumb config, or null if still loading.
 *
 * @example
 * // Reset point (auto-detected from BREADCRUMB_RESET_POINTS):
 * useBreadcrumb({ label: "Dashboard", route: "/" });
 *
 * @example
 * // Regular page (appends to trail):
 * useBreadcrumb({ label: pathTitle, route: `/path/${pathId}`, display: "page" });
 *
 * @example
 * // Loading state (null = don't touch the trail yet):
 * useBreadcrumb(pathData ? { label: pathData.title, route: `/path/${id}` } : null);
 */
export function useBreadcrumb(config: UseBreadcrumbConfig | null): void {
  const ctx = useBreadcrumbContextOptional();

  const prevConfigRef = useRef<string | null>(null);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  useEffect(() => {
    if (!ctx || config === null) return;

    const serialized = JSON.stringify(config);
    if (serialized === prevConfigRef.current) return;
    prevConfigRef.current = serialized;

    ctx.updateTrail(
      { label: config.label, route: config.route },
      config.display ?? "navbar",
      config.resetTrail
    );
  }, [config, ctx]);

  // On unmount only: hide breadcrumbs from display, preserve sessionStorage.
  // Uses a ref so cleanup fires only on actual unmount, not on every context change.
  useEffect(() => {
    return () => {
      ctxRef.current?._hideDisplay();
    };
  }, []);
}
